import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import "./ActionMenu.css";
import Button from "./Button";
import { Check, ChevronDown, MoreVertical } from "./icons";

/*
  ActionMenu — a "⋯" button that opens a short list of actions.
  With `triggerLabel` the trigger is a labelled button (icon + text + ▾)
  instead of an icon-only one, for toolbars of named action groups.

  items: [{ key, label, icon, onClick, tone: "danger" | undefined, disabled, hidden, divider, checked }]
    - hidden items are dropped (use it for actions the user cannot perform)
    - divider: true draws a separator before the item
    - checked (boolean) makes it a toggle (menuitemcheckbox) with a ✓ when on

  The list renders in a portal with fixed positioning, so card/section
  `overflow` never clips it, and it is kept inside the viewport (flips up
  near the bottom, clamps left/right). Keyboard: arrows, Home/End, Escape.
*/
const MARGIN = 8;
const ENTRY = "[role='menuitem']:not([disabled]), [role='menuitemcheckbox']:not([disabled])";

export default function ActionMenu({ items = [], label = "Actions", size = "sm", disabled = false, className = "", buttonClassName = "", icon, triggerLabel }) {
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState(null);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const menuId = useId();
  const visible = items.filter((item) => item && !item.hidden);

  const close = useCallback((focusTrigger = true) => {
    setOpen(false);
    setPosition(null);
    if (focusTrigger) triggerRef.current?.focus();
  }, []);

  const place = useCallback(() => {
    const trigger = triggerRef.current?.getBoundingClientRect();
    const menu = menuRef.current;
    if (!trigger || !menu) return;
    const { innerWidth: vw, innerHeight: vh } = window;
    const width = Math.min(menu.offsetWidth, vw - MARGIN * 2);
    const height = menu.offsetHeight;
    const rtl = getComputedStyle(triggerRef.current).direction === "rtl";
    let left = rtl ? trigger.left : trigger.right - width;
    left = Math.max(MARGIN, Math.min(left, vw - width - MARGIN));
    const below = trigger.bottom + 4;
    const top = below + height > vh - MARGIN && trigger.top - height - 4 > MARGIN ? trigger.top - height - 4 : Math.max(MARGIN, Math.min(below, vh - height - MARGIN));
    setPosition({ top, left, maxWidth: vw - MARGIN * 2, maxHeight: vh - MARGIN * 2 });
  }, []);

  useLayoutEffect(() => {
    if (open) place();
  }, [open, place]);

  useEffect(() => {
    if (!open) return undefined;
    const onPointer = (event) => {
      if (menuRef.current?.contains(event.target) || triggerRef.current?.contains(event.target)) return;
      close(false);
    };
    const onViewport = () => close(false);
    document.addEventListener("mousedown", onPointer);
    document.addEventListener("touchstart", onPointer);
    window.addEventListener("resize", onViewport);
    window.addEventListener("scroll", onViewport, true);
    return () => {
      document.removeEventListener("mousedown", onPointer);
      document.removeEventListener("touchstart", onPointer);
      window.removeEventListener("resize", onViewport);
      window.removeEventListener("scroll", onViewport, true);
    };
  }, [open, close]);

  useEffect(() => {
    if (open && position) menuRef.current?.querySelector(ENTRY)?.focus({ preventScroll: true });
  }, [open, position]);

  const onMenuKeyDown = (event) => {
    const entries = [...(menuRef.current?.querySelectorAll(ENTRY) || [])];
    const index = entries.indexOf(document.activeElement);
    const go = (next) => {
      event.preventDefault();
      entries[(next + entries.length) % entries.length]?.focus();
    };
    if (event.key === "Escape") {
      event.preventDefault();
      event.stopPropagation();
      close();
    } else if (event.key === "ArrowDown") go(index + 1);
    else if (event.key === "ArrowUp") go(index - 1);
    else if (event.key === "Home") go(0);
    else if (event.key === "End") go(entries.length - 1);
    else if (event.key === "Tab") close(false);
  };

  if (!visible.length) return null;
  const dir = triggerRef.current ? getComputedStyle(triggerRef.current).direction : undefined;

  return (
    <span className={`ui-action-menu ${className}`} onClick={(event) => event.stopPropagation()}>
      <Button
        ref={triggerRef}
        type="button"
        variant="primary"
        appearance="soft"
        size={size}
        rounded
        className={`ui-action-menu-trigger ${triggerLabel ? "has-label" : ""} ${buttonClassName}`}
        icon={icon || <MoreVertical size={16} />}
        aria-label={triggerLabel ? undefined : label}
        title={triggerLabel ? undefined : label}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        disabled={disabled}
        onClick={() => (open ? close() : setOpen(true))}
      >
        {triggerLabel && (
          <>
            {triggerLabel}
            <ChevronDown size={14} className="ui-action-menu-chevron" aria-hidden="true" />
          </>
        )}
      </Button>
      {open &&
        createPortal(
          <div
            ref={menuRef}
            id={menuId}
            role="menu"
            aria-label={label}
            dir={dir}
            className="ui-action-menu-popup"
            style={position ? { top: position.top, left: position.left, maxWidth: position.maxWidth, maxHeight: position.maxHeight } : { visibility: "hidden", top: 0, left: 0 }}
            onKeyDown={onMenuKeyDown}
          >
            {visible.map((item, index) => {
              const checkable = typeof item.checked === "boolean";
              return (
                <div key={item.key || index} role="none">
                  {item.divider && index > 0 && <div className="ui-action-menu-divider" role="separator" />}
                  <button
                    type="button"
                    role={checkable ? "menuitemcheckbox" : "menuitem"}
                    aria-checked={checkable ? item.checked : undefined}
                    className={`ui-action-menu-item ${item.tone ? `tone-${item.tone}` : ""} ${item.checked ? "is-checked" : ""}`}
                    disabled={item.disabled}
                    onClick={() => {
                      close();
                      item.onClick?.();
                    }}
                  >
                    {item.icon && (
                      <span className="ui-action-menu-icon" aria-hidden="true">
                        {item.icon}
                      </span>
                    )}
                    <span>{item.label}</span>
                    {checkable && (
                      <span className="ui-action-menu-check" aria-hidden="true">
                        {item.checked && <Check size={16} />}
                      </span>
                    )}
                  </button>
                </div>
              );
            })}
          </div>,
          document.body,
        )}
    </span>
  );
}
